import { OTHER_ID } from '@aera/schema';
import { tidy } from '@aera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, OptionCard, OtherField, fresh, pickOther, type AnswerProps } from './common.js';

/** single, multi, band and pick_explain; plus Other as a field typed into directly and the optional "say more" line. */
export function ChoiceQuestion({ rq, value, onChange, compact, onLimit, lang }: AnswerProps) {
  const { t } = useTranslation();
  const multi = rq.type === 'multi' || rq.type === 'pick_explain';
  const [limitHit, setLimitHit] = useState(false);
  const options = rq.options;
  const isExclusive = (id: string) => !!rq.options.find((o) => o.id === id)?.exclusive;

  const toggle = (id: string) => {
    const base = fresh(value);
    if (!multi) {
      onChange({ ...base, selected: [id], other: undefined });
      return;
    }
    const isOn = base.selected.includes(id);
    if (isOn) {
      onChange({ ...base, selected: base.selected.filter((s) => s !== id) });
      setLimitHit(false);
      return;
    }
    const exclusive = rq.options.find((o) => o.id === id)?.exclusive;
    let selected = exclusive ? [id] : [...base.selected.filter((s) => !rq.options.find((o) => o.id === s)?.exclusive), id];
    if (rq.max && selected.length > rq.max) {
      setLimitHit(true);
      onLimit?.();
      return;
    }
    setLimitHit(false);
    onChange({ ...base, selected, ...(selected.includes(OTHER_ID) ? {} : { other: undefined }) });
  };

  const typeOther = (other: string) => {
    const base = fresh(value);
    const selected = pickOther(base.selected, other, multi, isExclusive);
    // A legacy bank's maximum still holds: the text stays unpicked rather than going over it.
    if (multi && rq.max && selected.length > rq.max) {
      setLimitHit(true);
      onLimit?.();
      return;
    }
    onChange({ ...base, selected, other }, { debounce: true });
  };

  const otherOn = value.selected.includes(OTHER_ID);
  const count = options.length + (rq.allowOther ? 1 : 0);
  const groupRole = multi ? 'group' : 'radiogroup';
  const itemRole = multi ? 'checkbox' : 'radio';
  // Chips only in a shared screen's rows; a band on its own screen gets option cards, number keys and all.
  const asChips = !!compact;

  return (
    <div className="space-y-3">
      {multi && rq.max && (
        <p className={`text-[13px] ${limitHit ? 'text-indigo' : 'text-ink-2'}`} aria-live="polite">
          {value.selected.length > 0 ? t('question.picked', { n: value.selected.length, max: rq.max }) : t('question.pickUpTo', { max: rq.max })}
        </p>
      )}
      <div role={groupRole} aria-labelledby={`q-${rq.id}-label`} className={asChips ? 'flex flex-wrap gap-2' : 'grid gap-2.5'}>
        {options.map((o, i) =>
          asChips ? (
            <Chip key={o.id} role={itemRole} checked={value.selected.includes(o.id)} onClick={() => toggle(o.id)}>
              {tidy(o.label)}
            </Chip>
          ) : (
            <OptionCard
              key={o.id}
              id={`opt-${rq.id}-${i + 1}`}
              role={itemRole}
              checked={value.selected.includes(o.id)}
              onClick={() => toggle(o.id)}
              hotkey={count <= 9 ? i + 1 : undefined}
            >
              {tidy(o.label)}
            </OptionCard>
          ),
        )}
        {rq.allowOther && (
          <OtherField
            id={asChips ? undefined : `opt-${rq.id}-${options.length + 1}`}
            compact={asChips}
            lang={lang}
            hotkey={!asChips && count <= 9 ? options.length + 1 : undefined}
            checked={otherOn}
            value={value.other ?? ''}
            label={t('question.otherField', { lng: lang })}
            onChange={typeOther}
          />
        )}
      </div>
      {rq.explain && (
        <div className="pt-2">
          <label htmlFor={`explain-${rq.id}`} className="mb-1.5 block text-[14px] text-ink-2">
            {rq.explain}
          </label>
          <input
            id={`explain-${rq.id}`}
            type="text"
            maxLength={300}
            value={value.text ?? ''}
            placeholder={t('question.explainPlaceholder')}
            onChange={(e) => onChange({ ...fresh(value), text: e.target.value }, { debounce: true })}
            className="w-full rounded-2xl border border-hair bg-white px-4 py-3 text-[16px] outline-none transition-colors placeholder:text-ink-3 focus:border-indigo"
          />
        </div>
      )}
    </div>
  );
}
